/**
 * Global MQTT Synchronization Hook
 * 
 * Централизованная обработка всех MQTT событий для синхронизации состояния приложения.
 * Используется только в App.tsx для глобальной подписки.
 * 
 * Архитектура:
 * 1. Все MQTT события обрабатываются здесь
 * 2. Автоматическая инвалидация React Query кеша
 * 3. Умные уведомления (только важные события)
 * 4. Обновление глобального состояния (Zustand stores)
 * 5. Обработка блокировки пользователя
 */

import { useQueryClient } from '@tanstack/react-query';
import { useUserEvents, useBroadcastEvents, useMonitoringEvents } from './hooks';
import { useAuthStore } from '../store/authSlice';
import { EVENT_TYPES, UserBlockedPayload } from '../types/event';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

export function useGlobalMQTTSync() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const initializedRef = useRef(false);

  // Subscribe to user events (updates, groups changed, blocking)
  useUserEvents((event) => {
    console.log('[GlobalMQTTSync] User event:', event.type);

    // Handle user updated
    if (event.type === EVENT_TYPES.USER_UPDATED) {
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['users', user?.id] });
    }

    // Handle groups changed
    if (event.type === EVENT_TYPES.USER_GROUPS_CHANGED) {
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    }

    // Handle user blocked
    if (event.type === EVENT_TYPES.USER_BLOCKED) {
      const payload = event.payload as UserBlockedPayload;
      console.log('[GlobalMQTTSync] User blocked:', payload.reason);
      
      notifications.show({
        title: t('notifications.account_blocked', 'Account Blocked'),
        message: payload.reason || t('notifications.account_blocked_message', 'Your account has been blocked'),
        color: 'red',
        autoClose: false,
      });

      logout();
    }
  }, [queryClient, user?.id, t, logout]);

  // Subscribe to broadcast events for admins
  useBroadcastEvents(EVENT_TYPES.USER_CREATED, (event) => {
    console.log('[GlobalMQTTSync] User created:', event.payload);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [queryClient]);

  useBroadcastEvents(EVENT_TYPES.USER_UPDATED, (event) => {
    console.log('[GlobalMQTTSync] User updated (broadcast):', event.payload);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [queryClient]);

  useBroadcastEvents(EVENT_TYPES.USER_DELETED, (event) => {
    console.log('[GlobalMQTTSync] User deleted:', event.payload);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [queryClient]);

  useBroadcastEvents(EVENT_TYPES.GROUP_CREATED, (event) => {
    console.log('[GlobalMQTTSync] Group created:', event.payload);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  }, [queryClient]);

  useBroadcastEvents(EVENT_TYPES.GROUP_UPDATED, (event) => {
    console.log('[GlobalMQTTSync] Group updated:', event.payload);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  }, [queryClient]);

  useBroadcastEvents(EVENT_TYPES.GROUP_DELETED, (event) => {
    console.log('[GlobalMQTTSync] Group deleted:', event.payload);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  }, [queryClient]);

  // Plugin events are handled by their own providers (e.g., RealtorMQTTProvider)

  // Subscribe to system monitoring metrics
  useMonitoringEvents((event) => {
    if (event.type === 'monitoring.metrics') {
      // Invalidate monitoring queries to trigger re-render with new data
      queryClient.invalidateQueries({ queryKey: ['monitoring'] });
    }
  }, [queryClient]);

  // Log initialization
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    console.log('[GlobalMQTTSync] Initialized');
  }, []);
}
